import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/Breadcrumb';

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="About Us"
        description="Learn about Finclify - your comprehensive financial planning companion. We provide free calculators, comparisons, and guides to help you make informed financial decisions."
        keywords="about finclify, financial tools, finance calculators, financial planning, about us"
        type="website"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'About Us', url: '/about' }
        ]}
      />
      <Breadcrumb
        items={[
          { name: 'Home', url: '/' },
          { name: 'About Us', url: '/about' }
        ]}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-blue-100 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            About Finclify
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Empowering individuals to make informed financial decisions through comprehensive tools and expert guidance
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-12 space-y-8">
            
            {/* Mission */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-gray-600 leading-relaxed">
                At Finclify, we believe that financial literacy is the foundation of financial freedom. 
                Our mission is to provide accessible, accurate, and easy-to-use financial tools that help 
                individuals make informed decisions about their money. Whether you're planning for a home 
                loan, calculating your tax savings, or comparing financial products, we're here to simplify 
                the complex world of personal finance.
              </p>
            </div>

            {/* What We Offer */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">What We Offer</h2>
              <div className="grid md:grid-cols-3 gap-6 mt-6">
                <div className="card">
                  <div className="text-4xl mb-4">🧮</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Financial Calculators</h3>
                  <p className="text-gray-600">
                    Calculate EMI, plan investments, estimate tax savings, and more with our comprehensive suite of calculators.
                  </p>
                </div>
                <div className="card">
                  <div className="text-4xl mb-4">⚖️</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Product Comparisons</h3>
                  <p className="text-gray-600">
                    Compare credit cards, loans, and insurance plans to find the best options that suit your needs.
                  </p>
                </div>
                <div className="card">
                  <div className="text-4xl mb-4">📚</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Educational Guides</h3>
                  <p className="text-gray-600">
                    Learn from expert guides on tax saving, investment strategies, and stock market basics.
                  </p>
                </div>
              </div>
            </div>

            {/* Our Values */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Values</h2>
              <ul className="space-y-4 text-gray-600">
                <li className="flex items-start">
                  <span className="text-primary-600 mr-3 font-bold">✓</span>
                  <span><strong className="text-gray-900">Accuracy:</strong> We ensure all our calculators and tools provide accurate results based on current financial regulations and formulas.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-3 font-bold">✓</span>
                  <span><strong className="text-gray-900">Accessibility:</strong> Our tools are free to use and designed to be user-friendly for everyone, regardless of their financial knowledge.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-3 font-bold">✓</span>
                  <span><strong className="text-gray-900">Privacy:</strong> We respect your privacy. All calculations are performed locally in your browser - we don't store your personal financial data.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-3 font-bold">✓</span>
                  <span><strong className="text-gray-900">Education:</strong> We believe in empowering users through knowledge, not just providing tools.</span>
                </li>
              </ul>
            </div>

            {/* Why Choose Us */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Finclify?</h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  <strong className="text-gray-900">Comprehensive Suite:</strong> From loan calculators to investment planning, 
                  we offer a wide range of tools to cover all your financial planning needs.
                </p>
                <p>
                  <strong className="text-gray-900">Always Free:</strong> All our tools and resources are completely free to use. 
                  No hidden charges, no premium tiers - just free financial tools for everyone.
                </p>
                <p>
                  <strong className="text-gray-900">Regular Updates:</strong> We continuously update our tools and content to 
                  reflect the latest financial regulations, interest rates, and market trends.
                </p>
                <p>
                  <strong className="text-gray-900">Privacy-First:</strong> Your financial data never leaves your device. 
                  All calculations happen locally, ensuring complete privacy and security.
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-200 rounded-xl p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Take Control of Your Finances?</h3>
              <p className="text-gray-600 mb-6">
                Start using our tools today and make informed financial decisions
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/calculators/emi"
                  className="btn-primary text-lg px-8 py-3 rounded-xl"
                >
                  Calculate EMI
                </Link>
                <Link
                  to="/guides/investment-strategies"
                  className="btn-secondary text-lg px-8 py-3 rounded-xl"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;

